import React, { useState } from 'react';
import { useMembership } from '../../context/MembershipContext';
import { FaCrown, FaCheck, FaTimes, FaStar, FaCreditCard, FaUser, FaArrowUp } from 'react-icons/fa';
import UpgradeToPremiumModal from './UpgradeToPremiumModal';

const UpgradeModal = ({ isOpen, onClose, onUpgrade }) => {
  const { 
    availablePlans, 
    loading, 
    error, 
    updateMembership,
    membershipStatus,
    canUpgrade,
    isFree,
    isPremium
  } = useMembership();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    
    // Si es un upgrade a premium, usar el nuevo modal
    if (selectedPlan.type === 'premium' && canUpgrade()) {
      setShowUpgradeModal(true);
      return;
    }
    
    setUpgrading(true);
    try {
      const result = await updateMembership(selectedPlan.id);
      if (result.success) {
        onUpgrade?.(selectedPlan);
        onClose();
      }
    } catch (error) {
      console.error('Error al actualizar membresía:', error);
    } finally {
      setUpgrading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    onUpgrade?.({ name: 'Premium', type: 'premium' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <FaCrown className="text-yellow-500 text-2xl mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Actualizar Membresía</h2>
                <p className="text-gray-600">Elige el plan que mejor se adapte a tus necesidades</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando planes disponibles...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-500 mb-4">
                  <FaTimes className="text-4xl mx-auto" />
                </div>
                <p className="text-red-600">Error al cargar los planes</p>
                <p className="text-gray-600 text-sm mt-2">{error}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Plan Gratuito */}
                <div className="border-2 border-gray-200 rounded-lg p-6 relative">
                  <div className="text-center mb-4">
                    <FaUser className="text-4xl text-gray-400 mx-auto mb-2" />
                    <h3 className="text-xl font-semibold text-gray-900">Plan Gratuito</h3>
                    <p className="text-gray-600">Características básicas</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <span className="text-3xl font-bold text-gray-900">$0</span>
                    <span className="text-gray-600">/mes</span>
                  </div>

                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm">
                      <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                      <span>Acceso básico a la plataforma</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                      <span>Consultas limitadas</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                      <span>Soporte básico</span>
                    </li>
                  </ul>

                  <button
                    disabled
                    className="w-full py-3 px-4 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed"
                  >
                    {isFree ? 'Plan Actual' : 'Plan Gratuito'}
                  </button>
                </div>

                {/* Plan Premium */}
                {availablePlans.map((plan) => (
                  <div 
                    key={plan.id}
                    className={`border-2 rounded-lg p-6 relative cursor-pointer transition-all ${
                      selectedPlan?.id === plan.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {/* Popular Badge */}
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                          <FaStar className="mr-1" />
                          Más Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-4">
                      <FaCrown className="text-4xl text-yellow-500 mx-auto mb-2" />
                      <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                      <p className="text-gray-600">{plan.description}</p>
                    </div>
                    
                    <div className="text-center mb-6">
                      <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-600">/{plan.period}</span>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm">
                          <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                        selectedPlan?.id === plan.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {selectedPlan?.id === plan.id ? 'Seleccionado' : 'Seleccionar Plan'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            {selectedPlan && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      Plan seleccionado: <span className="font-semibold text-gray-900">{selectedPlan.name}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                      Precio: <span className="font-semibold text-gray-900">${selectedPlan.price}/{selectedPlan.period}</span>
                    </p>
                    {selectedPlan.type === 'premium' && canUpgrade() && (
                      <p className="text-sm text-blue-600 font-medium">
                        <FaArrowUp className="inline mr-1" />
                        Upgrade único disponible
                      </p>
                    )}
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={onClose}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleUpgrade}
                      disabled={upgrading}
                      className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {upgrading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Actualizando...
                        </>
                      ) : (
                        <>
                          <FaCreditCard className="mr-2" />
                          {selectedPlan.type === 'premium' && canUpgrade() ? 'Upgrade a Premium' : 'Actualizar Ahora'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Upgrade a Premium */}
      <UpgradeToPremiumModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgradeSuccess={handleUpgradeSuccess}
      />
    </>
  );
};

export default UpgradeModal; 